/**
 * Audit Service for frontend
 * Handles all audit-related API calls and client-side logging
 */

import { apiClient } from './apiClient';
import {
  AuditEvent,
  AuditSearchCriteria,
  AuditSearchResult,
  AuditStats,
  AuditCategory,
  AuditLevel
} from '../types/audit';

class AuditService {
  private correlationId: string = '';
  private sessionId: string = '';
  private userId: string = '';

  constructor() {
    // Generate session ID on initialization
    this.sessionId = this.generateSessionId();
  }

  /**
   * Generate an RFC 4122 version 4 UUID using cryptographically secure browser randomness.
   */
  private generateSecureId(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(16));

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `session_${this.generateSecureId()}`;
  }

  /**
   * Generate a unique correlation ID for each request
   */
  private generateCorrelationId(): string {
    return this.generateSecureId();
  }

  /**
   * Set user context for audit logging
   */
  public setUserContext(userId: string): void {
    this.userId = userId;
  }

  /**
   * Get current correlation ID
   */
  public getCorrelationId(): string {
    if (!this.correlationId) {
      this.correlationId = this.generateCorrelationId();
    }
    return this.correlationId;
  }

  /**
   * Reset correlation ID for new request
   */
  public resetCorrelationId(): void {
    this.correlationId = this.generateCorrelationId();
  }

  /**
   * Search audit logs
   */
  public async searchAuditLogs(criteria: AuditSearchCriteria): Promise<AuditSearchResult> {
    try {
      const body = {
        startDate: criteria.startDate?.toISOString(),
        endDate: criteria.endDate?.toISOString(),
        user: criteria.userId,
        level: criteria.level,
        category: criteria.category,
        search: criteria.searchText,
        limit: criteria.pageSize || 100,
        offset: 0
      };

      const response = await apiClient.post<{
        logs: any[];
        totalCount: number;
        count: number;
        continuationToken?: string;
      }>('/logs/search', body);

      if (response.success && response.data) {
        // Map backend response to expected format
        return {
          events: response.data.logs || [],
          totalCount: response.data.totalCount || 0,
          continuationToken: response.data.continuationToken
        };
      }

      throw new Error(response.message || 'Failed to search audit logs');
    } catch (error: any) {
      console.error('Failed to search audit logs:', error);
      // Provide more detailed error information
      if (error.message?.includes('500') || error.message?.includes('HTTP 500')) {
        throw new Error('Audit log service is temporarily unavailable. The database may be initializing. Please refresh the page and try again.');
      }
      throw error;
    }
  }

  /**
   * Get audit statistics
   */
  public async getAuditStats(timeRange: string = '24h'): Promise<AuditStats> {
    try {
      const response = await apiClient.get(`/audit/stats?timeRange=${timeRange}`);

      if (response.success && response.data) {
        return response.data as AuditStats;
      }

      throw new Error(response.message || 'Failed to get audit statistics');
    } catch (error) {
      console.error('Failed to get audit statistics:', error);
      throw error;
    }
  }

  /**
   * Get audit summary for a specific user
   */
  public async getUserAuditSummary(userId: string): Promise<any> {
    try {
      const response = await apiClient.get(`/audit/summary/${userId}`);

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to get user audit summary');
    } catch (error) {
      console.error('Failed to get user audit summary:', error);
      throw error;
    }
  }

  // Helper function to escape CSV values
  escapeCSV = (value: any): string => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Helper to stringify complex objects for CSV
  stringifyForCSV = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  private getBasicEventFields(event: any): string[] {
    return [
      new Date(event.timestamp).toISOString(),
      event.id || '',
      event.userEmail || '',
      event.userName || '',
      event.action || '',
      event.category || '',
      event.level || '',
      event.result || '',
      event.resourceType || '',
      event.resourceId || '',
      event.resource || '',
      event.source || ''
    ];
  }

  private getTechnicalEventFields(event: any): string[] {
    return [
      event.ipAddress || '',
      event.browserType || '',
      event.operatingSystem || '',
      event.correlationId || '',
      event.sessionId || '',
      event.duration?.toString() || ''
    ];
  }

  private getRequestEventFields(event: any): string[] {
    return [
      event.method || '',
      event.path || '',
      event.statusCode?.toString() || '',
      event.summary || '',
      this.stringifyForCSV(event.details),
      this.stringifyForCSV(event.errorDetails)
    ];
  }

  private mapSingleEventToCSVRow(event: any): string[] {
    const fields = [
      ...this.getBasicEventFields(event),
      ...this.getTechnicalEventFields(event),
      ...this.getRequestEventFields(event)
    ];
    return fields.map(this.escapeCSV);
  }

  private mapEventsToCSVRows(searchResult: AuditSearchResult): string[][] {
    const rows: string[][] = [];
    for (const event of searchResult.events) {
      rows.push(this.mapSingleEventToCSVRow(event));
    }
    return rows;
  }

  /**
   * Export audit logs
   */
  public async exportAuditLogs(format: 'csv' | 'json', criteria: AuditSearchCriteria): Promise<Blob> {
    try {
      // For now, we'll fetch the data and create a blob client-side
      const searchResult = await this.searchAuditLogs(criteria);

      let content: string;
      if (format === 'json') {
        content = JSON.stringify(searchResult.events, null, 2);
      } else {
        // CSV format with proper escaping - include all relevant fields
        const headers = [
          'Timestamp',
          'ID',
          'User Email',
          'User Name',
          'Action',
          'Category',
          'Level',
          'Result',
          'Resource Type',
          'Resource ID',
          'Resource',
          'Source',
          'IP Address',
          'Browser',
          'Operating System',
          'Correlation ID',
          'Session ID',
          'Duration (ms)',
          'Method',
          'Path',
          'Status Code',
          'Summary',
          'Details',
          'Error Details'
        ];
        const rows = this.mapEventsToCSVRows(searchResult);
        content = [headers.map(this.escapeCSV), ...rows].map(row => row.join(',')).join('\n');
      }

      return new Blob([content], { type: format === 'json' ? 'application/json' : 'text/csv' });
    } catch (error) {
      console.error('Failed to export audit logs:', error);
      throw error;
    }
  }

  /**
   * Query Application Insights using KQL
   */
  public async queryApplicationInsights(query: string, timespan?: string): Promise<any> {
    try {
      const response = await apiClient.post('/audit/query', {
        query,
        timespan
      });

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to query Application Insights');
    } catch (error) {
      console.error('Failed to query Application Insights:', error);
      throw error;
    }
  }

  /**
   * Log a client-side event
   */
  public logClientEvent(
    action: string,
    category: AuditCategory = AuditCategory.UserAction,
    level: AuditLevel = AuditLevel.Info,
    metadata?: Record<string, any>
  ): void {
    try {
      // Create audit event
      const event: Partial<AuditEvent> = {
        timestamp: new Date(),
        correlationId: this.getCorrelationId(),
        userId: this.userId || 'anonymous',
        sessionId: this.sessionId,
        action,
        category,
        level,
        source: 'Frontend',
        userAgent: navigator.userAgent,
        metadata: metadata || {}
      };

      // Log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[AUDIT]', event);
      }

      // Send to backend audit log (fire and forget)
      apiClient.post('/logs/client-event', event).catch(error => {
        // Silently fail - don't disrupt user experience for logging failures
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to log client event:', error);
        }
      });
    } catch (error) {
      console.error('Error logging client event:', error);
    }
  }

  /**
   * Log user navigation
   */
  public logNavigation(from: string, to: string, metadata?: Record<string, any>): void {
    this.logClientEvent(
      `Navigate:${to}`,
      AuditCategory.UserAction,
      AuditLevel.Verbose,
      {
        fromPath: from,
        toPath: to,
        ...metadata
      }
    );
  }

  /**
   * Log user interaction
   */
  public logInteraction(
    component: string,
    action: string,
    metadata?: Record<string, any>
  ): void {
    this.logClientEvent(
      `${component}:${action}`,
      AuditCategory.UserAction,
      AuditLevel.Verbose,
      metadata
    );
  }

  /**
   * Log error
   */
  public logError(
    action: string,
    error: any,
    metadata?: Record<string, any>
  ): void {
    this.logClientEvent(
      action,
      AuditCategory.UserAction,
      AuditLevel.Error,
      {
        error: {
          message: error.message || String(error),
          stack: error.stack,
          name: error.name
        },
        ...metadata
      }
    );
  }

  /**
   * Log API call
   */
  public logApiCall(
    method: string,
    path: string,
    statusCode: number,
    duration: number,
    metadata?: Record<string, any>
  ): void {
    this.logClientEvent(
      `API:${method} ${path}`,
      AuditCategory.Integration,
      statusCode >= 400 ? AuditLevel.Error : AuditLevel.Verbose,
      {
        method,
        path,
        statusCode,
        duration,
        ...metadata
      }
    );
  }

  /**
   * Add correlation ID to request headers
   */
  public addCorrelationHeader(headers: Record<string, string>): Record<string, string> {
    return {
      ...headers,
      'x-correlation-id': this.getCorrelationId(),
      'x-session-id': this.sessionId
    };
  }
}

// Export singleton instance
export const auditService = new AuditService();